import React, { useEffect } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import moment from "moment"
import "../../common/Charts/AnalysisCharts.scss"

const AnalysisChart = (props) => {

  const {
    startTime,
    endTime,
    tubingPressure,
    casingPressure,
    linePressure,
    gasRate,
    state,
    batteryVoltage,
    solarVoltage,
    adHoc,
    unit

  } = props.selectedValue
  const AUX1 = props.selectedValue.aux1;
  const AUX2 = props.selectedValue.aux2;
  const AUX3 = props.selectedValue.aux3;
  const MODE = props.selectedValue.mode;
  const hasIntermittentMode = props?.isIntermittentMode

  const dataObjects = {
    "AUX1": AUX1,
    "AUX2": AUX2,
    "AUX3": AUX3,
    "MODE": MODE
  };
  function generateDataStringForPressure(tubingPressure, casingPressure, linePressure, aux1, aux2, aux3, mode) {
    // Convert boolean value to string representation
    let tubingPressures = tubingPressure ? 'Tubing_Pressure' : "";
    let casingPressures = casingPressure ? 'Casing_Pressure' : "";
    let linePressures = linePressure ? 'Line_Pressure' : "";
    let AUX1 = props.selectedValue?.aux1?.label?.toLowerCase() !== "disabled" && aux1 ? 'AUX1' : "";
    let AUX2 = props.selectedValue?.aux2?.label?.toLowerCase() !== "disabled" && aux2 ? 'AUX2' : "";
    let AUX3 = props.selectedValue?.aux3?.label?.toLowerCase() !== "disabled" && aux3 ? 'AUX3' : "";
    let MODE = props.selectedValue.mode?.label?.toLowerCase() !== "disabled" && mode ? 'MODE' : ""

    // Construct the comma-separated string
    var dataString = [tubingPressures, casingPressures, linePressures, AUX1, AUX2, AUX3, MODE].filter(Boolean).join(',');

    return dataString;
  }

  function getBaseIntervalCount(duration) {
    let count = 1;
    switch (duration) {
      case "-4h":
        count = 5;
        break;
      case "-12h":
        count = 10;
        break;
      case "-24h":
        count = 30;
        break;
      default:
        count = 1;
    }

    return count;

  }

  function generateDataStringForVoltage(batteryVoltage, solarVoltage) {
    // Convert boolean value to string representation
    //let states = state ? 'State' : '';
    let batteryVoltages = batteryVoltage ? 'Battery Voltage' : '';
    let solarVoltages = solarVoltage ? 'Solar Voltage' : '';

    // Construct the comma-separated string
    var dataString = [batteryVoltages, solarVoltages].filter(Boolean).join(',');

    return dataString;
  }

  function getStateEvents(data) {

    const groupedData = [];

    let currentState = null;
    let startTime = null;

    for (const item of data) {
      if (item.State !== null) {
        if (currentState === null) {
          // If currentState is null, initialize currentState and startTime
          currentState = item.State;
          startTime = item.Time;
        } else if (currentState === item.State) {
          // If currentState matches item.State, update only the endTime
          // if (groupedData.length > 0) {
          //   groupedData[groupedData.length - 1].endTime = item.Time;
          //}
        } else {
          // If currentState doesn't match item.State, push the previous state event
          groupedData.push({ State: currentState, startTime, endTime: item.Time });
          // Update currentState and startTime for the new state event
          currentState = item.State;
          startTime = item.Time;
        }
      }
    }

    // Push the last state event
    if (currentState !== null) {
      groupedData.push({ State: currentState, startTime, endTime: data[data.length - 1].Time });
    }

    return groupedData;
  }

  function formatLocalDate(timestamp) {
    const date = new Date(timestamp);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.000Z`;
  }


  // Function to convert timestamp to date string
  function formatDate(timestamp) {
    const date = new Date(timestamp);
    return date; // Convert to local date and time format
  }

  const formattedData = props?.trendData?.measurement?.map(item => {
    return {
      ...item,
      Time: formatDate(item.Time)
    };
  });

  function getFormattedData(chartData) {
    return chartData?.map(item => {
      return {
        ...item,
        Time: formatDate(item.Time)
      };
    });
  }


  function getSeriesShades(series) {
    let shadeColor = "";
    switch (series) {
      case "Tubing_Pressure":
        shadeColor = "#2B67D3";
        break;
      case "Casing_Pressure":
        shadeColor = "#B42318";
        break;
      case "Gas_Rate":
        shadeColor = "#027A48";
        break;
      case "Line_Pressure":
        shadeColor = "#E84513";
        break;
      case "Battery Voltage":
        shadeColor = "#8700DB";
        break;
      case "Solar Voltage":
        shadeColor = "#E18595";
        break;
      case "Ad_Hoc":
        shadeColor = "#FF0182";
        break;
      case "AUX1":
        shadeColor = "#6CE9A6";
        break;
      case "AUX2":
        shadeColor = "#FFB217";
        break;
      case "AUX3":
        shadeColor = "#90FF00";
        break;
      case "MODE":
        shadeColor = "#33FFF6";
        break;
      default:
        shadeColor = "#f20d0d";
    }
    return shadeColor;
  }

  const seriesNameToKey = {
    "Tubing_Pressure": "tubingPressure",
    "Casing_Pressure": "casingPressure",
    "Line_Pressure": "linePressure",
    "Gas_Rate": "gasRate",
    "Ad_Hoc": "adHoc",
    "Battery Voltage": "battery",
    "Solar Voltage": "solar",
    //"MODE": "mode"
  };


  function createCrossHatchPattern(root, baseColor) {
    return am5.Pattern.new(root, {
      color: am5.color(baseColor),
      fillOpacity: 0.3,
      strokeWidth: 1,
      width: 10,
      height: 10,
      rotation: 0
    });
  }

  // Alternative: Diagonal lines pattern
  function createDiagonalHatchPattern(root, baseColor) {
    return am5.Pattern.new(root, {
      color: am5.color(baseColor),
      fillOpacity: 0.3,
      strokeWidth: 1,
      width: 10,
      height: 10
    });
  }

  function setShadeColor(state) {

    let shadeColor = "";
    switch (state) {
      case 1: //close/fall
        shadeColor = "#f20d0d"; //red
        break;
      case 2: //open/fall (bypass)
        shadeColor = "#f2e70d"; //yellow
        break;
      case 3: //close/POB
        shadeColor = hasIntermittentMode ? "#f20d0d" : "#f2e70d"; //yellow
        break;
      case 4: //close/POB (bypass)
        shadeColor = "#0dc1f2"; //changed to red as per the list
        break;
      case 5: //open/arriving
        shadeColor = "#0dc1f2"; //blue
        break;
      case 6://open/slug
        shadeColor = "#ffffffff"; //white
        break;
      case 7://open/sales
        shadeColor = "#1cf20d"; //green
        break;
      case 8://open/manual
        shadeColor = "#0da6f2"; //cross - hatched blue NEW
        break;
      case 9://close/manual
        shadeColor = "#f20d0d"; //cross - hatched red NEW
        break;
      case 10: //Close/ESD
        shadeColor = "#f20d0d"; //red (diagonal stripes) - NEW
        break;
      case 11: //Shut-in: Non Arrivals
        shadeColor = "#f20d0d"; //red (diagonal stripes) - NEW
        break;
      case 12: //Close/Recovery
        shadeColor = "#f20d0d"; //red - NEW
        break;
      case 13: //Close/Recovery POB 
        shadeColor = "#f20d0d"; //red - NEW
        break;
      case 14: //PWR SVR Shut-in
        shadeColor = "#f20d0d"; //red (diagonal stripes) - NEW
        break;
      case 15: //Standby
        shadeColor = "#A9A9A9"; //gray (diagonal stripes) - NEW
        break;
      case 16: //hold open
        shadeColor = "#0da6f2"; //cross - hatched blue - NEW
        break;
      case 17: //hold close
        shadeColor = "#f20d0d"; //cross - hatched red - NEW
        break;
        case 18: //High LP Shut-in
        shadeColor = "#f20d0d"; //red (diagonal stripes) - NEW
        break;
      default:
        shadeColor = "#f20d0d"; //red
    }
    return shadeColor;
  }


  const nameMap = {
    "Tubing_Pressure": "Tubing Pressure",
    "Casing_Pressure": "Casing Pressure",
    "Gas_Rate": "Gas Rate",
    "Line_Pressure": "Line Pressure",
    "Ad_Hoc": "Ad Hoc",
  }
  useEffect(() => {

    // Create root element
    am5.addLicense("AM5C137048159");
    let root = am5.Root.new("chartdiv");
    root.numberFormatter.set("numberFormat", "#,###.00");

    // Set themes
    root.setThemes([am5themes_Animated.new(root)]);

    // Create chart
    let chart = am5xy.XYChart.new(root, {
      focusable: true,
      panX: false,
      panY: false,
      wheelX: "none",
      wheelY: "none",
      pinchZoomX: true
    });
    chart.plotContainer.set("background", am5.Rectangle.new(root, {
      fill: am5.color(0xFFFFFF)
    }));

    root.container.children.push(chart);

    let colors = chart?.get("colors");
    if (colors) {
      colors.set("step", 3);
    }

    let xAxis = chart.xAxes.push(
      am5xy.DateAxis.new(root, {
        maxDeviation: 0.1,
        groupData: false,
        baseInterval: {
          timeUnit: props.baseTimeInterval,
          count: getBaseIntervalCount(props.selectedValue.startDateTime)
        },
        renderer: am5xy.AxisRendererX.new(root, {
          minGridDistance: 40,
          pan: "zoom",
          minorGridEnabled: true
        }),
        tooltip: am5.Tooltip.new(root, {}),
        dateFormats: {
          millisecond: 'MMM dd HH:mm:ss',
          second: 'MMM dd HH:mm:ss',
          minute: 'MMM dd HH:mm',
          hour: 'MMM dd HH',
          day: 'MMM dd',
          week: 'MMM dd HH:mm:ss',
          month: 'MMM dd HH:mm:ss',
          quarter: 'MMM dd HH:mm:ss',
          year: 'MMM dd HH:mm:ss',
        }
      })
    );

    let xRenderer = xAxis.get("renderer");
    xRenderer.labels.template.setAll({
      rotation: -45,
      centerY: am5.p50
    });


    const zoomEnded = () => {
      const selectionMin = xAxis.getPrivate("selectionMin");
      const selectionMax = xAxis.getPrivate("selectionMax");
      const StartDate: any = formatLocalDate(selectionMin);
      const EndTime: any = formatLocalDate(selectionMax);
      props?.setAutoRefresh(false)
      props.setSelectedValue({ ...props.selectedValue, startDateTime: StartDate, endDateTime: EndTime })
    }

    const handleStartEndChange = () => {
      let startEndChangeTimeout;
      if (startEndChangeTimeout) {
        clearTimeout(startEndChangeTimeout);
      }
      startEndChangeTimeout = setTimeout(function () {
        zoomEnded();
      }, 2000);
    }

    xAxis.on("start", handleStartEndChange);
    xAxis.on("end", handleStartEndChange);
    let chartData = formattedData as [];
    if (typeof chartData === "undefined")
      chartData = [];
    function createAxisAndSeries(position: 'left' | 'right', axisTitle: string, valueAxis: string) {

      let yRenderer = am5xy.AxisRendererY.new(root, {
        opposite: position === 'right' // Set opposite based on position
      });
      let yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          // min: 0,
          // max: 10000,

          renderer: yRenderer,
        })
      );


      yAxis.get("renderer").labels.template.setAll({
        fill: am5.color(0xFFFFFF),

      });

      yAxis.get("renderer").grid.template.setAll({
        stroke: am5.color(0xFFFFFF)
      });

      if (axisTitle === "Pressures(psig)" || axisTitle === "Pressures(kPag)")
        yRenderer.grid.template.set("strokeOpacity", 0.03);
      else
        yRenderer.grid.template.set("strokeOpacity", 0.0);
      //yRenderer.labels.template.set("fill", am5.color("#000000"));
      yRenderer.setAll({
        stroke: am5.color("#000000"),
        strokeOpacity: 0,
        opacity: 0
      });
      if (position === "left") {

        var label = am5.Label.new(root, {
          rotation: -90,
          text: axisTitle,
          y: am5.p50,
          centerX: am5.p50,
          fill: am5.color(0xFFFFFF)
          //x: am5.p0,
          //centerY: am5.p0
        })

        yAxis.children.unshift(
          label
        );
      }
      else {

        var label = am5.Label.new(root, {
          rotation: 90,
          text: axisTitle,
          y: am5.p50,
          centerX: am5.p50,
          fill: am5.color(0xFFFFFF)
        })

        yAxis.children.unshift(
          label
        );

      }

      if (position === 'left') {
        chart.yAxes.moveValue(yAxis, 0); // Move the left axis to the beginning
      }

      if (chart.yAxes.indexOf(yAxis) > 0) {
        yAxis.set("syncWithAxis", chart.yAxes.getIndex(0) as any);
      }

      // Add series

      if (axisTitle === "Pressures(psig)" || axisTitle === "Pressures(kPag)" || axisTitle === "State/Voltage" || axisTitle === "Ad Hoc" || axisTitle === "Gas Rate(mscfd)" || axisTitle === "Gas Rate(E3m3/d)") {

        let allCategories: string[] = valueAxis.split(",");
        allCategories.forEach(element => {
          let series = chart.series.push(
            am5xy.LineSeries.new(root, {
              xAxis: xAxis,
              yAxis: yAxis,
              name: element.includes("AUX") || element.includes("MODE") ? dataObjects[element].label : element,
              valueYField: axisTitle === "State/Voltage" ? element.split(" ")[0] : element,
              valueXField: "Time",
              minBulletDistance: 60,
              legendLabelText: "[bold {stroke}]{name}:[/]",
              legendRangeLabelText: "[{stroke}]{name}:[/]",
              legendValueText: "[bold {stroke}]{valueY}[/]",
              legendRangeValueText: "[{stroke}]{valueYClose}[/]",
              stroke: am5.color(getSeriesShades(element))
              // tooltip: am5.Tooltip.new(root, {
              //   pointerOrientation: "horizontal",
              //   labelText: element + " : {valueY}"
              // })
            })
          );

          series.strokes.template.setAll({ strokeWidth: 1 });

          series.bullets.push(function () {
            return am5.Bullet.new(root, {
              sprite: am5.Circle.new(root, {
                radius: 5,
                fill: am5.color(getSeriesShades(element))
              })
            });
          });

          // Set up data processor to parse string dates
          series.data.processor = am5.DataProcessor.new(root, {
            dateFormat: "yyyy-MM-dd HH:MM",
            dateFields: ["Time"]
          });

          if (element.includes("AUX") || element.includes("MODE"))
            series.data.setAll(getFormattedData(props?.trendData?.[element.toLowerCase()]) || []);
          else
            series.data.setAll(chartData);
        });
      }

      else {
        let series = chart.series.push(
          am5xy.LineSeries.new(root, {
            xAxis: xAxis,
            yAxis: yAxis,
            valueYField: valueAxis,
            valueXField: "Time",
            minBulletDistance: 60,
            name: valueAxis,
            legendLabelText: "[bold {stroke}]{name}:[/]",
            legendRangeLabelText: "[{stroke}]{name}:[/]",
            legendValueText: "[bold {stroke}]{valueY}[/]",
            legendRangeValueText: "[{stroke}]{valueYClose}[/]",
            stroke: am5.color(getSeriesShades(valueAxis))
            // tooltip: am5.Tooltip.new(root, {
            //   pointerOrientation: "horizontal",
            //   labelText: valueAxis + " : {valueY}"
            // })
          })
        );

        series.strokes.template.setAll({ strokeWidth: 1 });
        series.bullets.push(function () {
          return am5.Bullet.new(root, {
            sprite: am5.Circle.new(root, {
              radius: 5,
              fill: am5.color(getSeriesShades(valueAxis))
            })
          });
        });


        // Set up data processor to parse string dates
        series.data.processor = am5.DataProcessor.new(root, {
          dateFormat: "yyyy-MM-dd HH:MM", // Adjusted to reflect 24-hour time format
          dateFields: ["date"]
        });

        series.data.setAll(chartData);
      }
    }

    function createAxisAndSeriesState(position: 'left' | 'right' | '', axisTitle: string, valueAxis: string) {
      let yRenderer = am5xy.AxisRendererY.new(root, {
        opposite: position === 'right' // Set opposite based on position
      });
      let yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          // min: 0,
          // max: 10000,

          renderer: yRenderer,
        })
      );

      yRenderer.labels.template.set('visible', false);
      yRenderer.grid.template.set("strokeOpacity", 0.0);
      // yRenderer.labels.template.set("fill", am5.color("#000000"));
      yRenderer.setAll({
        stroke: am5.color("#000000"),
        strokeOpacity: 0,
        opacity: 0
      });
      if (position === "left") {

        var label = am5.Label.new(root, {
          rotation: -90,
          text: "",
          y: am5.p50,
          centerX: am5.p50,
          fill: am5.color("#000000")
          //x: am5.p0,
          //centerY: am5.p0
        })

        yAxis.children.unshift(
          label
        );
      }
      else {

        var label = am5.Label.new(root, {
          rotation: 90,
          text: axisTitle,
          y: am5.p50,
          centerX: am5.p50,
          fill: am5.color("#000000")
        })

        yAxis.children.unshift(
          label
        );

      }

      if (position === 'left') {
        chart.yAxes.moveValue(yAxis, 0); // Move the left axis to the beginning
      }

      if (chart.yAxes.indexOf(yAxis) > 0) {
        yAxis.set("syncWithAxis", chart.yAxes.getIndex(0) as any);
      }

      // Add series

      if (axisTitle === "Pressures(psig)" || axisTitle === "Pressures(kPag)" || axisTitle === "State/Voltage" || axisTitle === "State" || axisTitle == "Ad Hoc" || axisTitle === "Gas Rate(mscfd)" || axisTitle === "Gas Rate(E3m3/d)") {

        let allCategories: string[] = valueAxis.split(",");
        allCategories.forEach(element => {
          let series = chart.series.push(
            am5xy.LineSeries.new(root, {
              xAxis: xAxis,
              yAxis: yAxis,
              name: element,
              valueYField: element,
              valueXField: "Time",
              minBulletDistance: 60,
              opacity: 0,
            })
          );

          series.strokes.template.setAll({ strokeWidth: 0 });

          series.bullets.push(function () {
            return am5.Bullet.new(root, {
              sprite: am5.Circle.new(root, {
                radius: 0,
                fill: series.get("fill")
              })
            });
          });

          // Set up data processor to parse string dates
          series.data.processor = am5.DataProcessor.new(root, {
            dateFormat: "yyyy-MM-dd HH:MM",
            dateFields: ["Time"]
          });

          series.data.setAll(chartData);

        });
      }

      else {

        let series = chart.series.push(
          am5xy.LineSeries.new(root, {
            xAxis: xAxis,
            yAxis: yAxis,
            valueYField: valueAxis,
            valueXField: "Time",
            minBulletDistance: 60,
            name: valueAxis,

          })
        );

        series.strokes.template.setAll({ strokeWidth: 0 });
        series.bullets.push(function () {
          return am5.Bullet.new(root, {
            sprite: am5.Circle.new(root, {
              radius: 0,
              fill: series.get("fill")
            })
          });
        });


        // Set up data processor to parse string dates
        series.data.processor = am5.DataProcessor.new(root, {
          dateFormat: "yyyy-MM-dd HH:MM", // Adjusted to reflect 24-hour time format
          dateFields: ["date"]
        });

        series.data.setAll(chartData);
      }
    }

    const pressureLabel = typeof unit?.value === "string" && unit?.value?.toLowerCase() === "imperial"
      ? "Pressures(psig)"
      : "Pressures(kPag)";

    const gasRateLabel = typeof unit?.value === "string" && unit?.value?.toLowerCase() === "imperial" ? "Gas Rate(mscfd)" : "Gas Rate(E3m3/d)"

    if (tubingPressure || casingPressure || linePressure || AUX1.checked || AUX2.checked || AUX3.checked || MODE.checked)
      createAxisAndSeries('left', pressureLabel, generateDataStringForPressure(tubingPressure, casingPressure, linePressure, AUX1.checked, AUX2.checked, AUX3.checked, MODE.checked))
    if (gasRate) createAxisAndSeries('left', gasRateLabel, "Gas_Rate");
    if (adHoc) createAxisAndSeries('right', "Ad Hoc", "Ad_Hoc")
    if (batteryVoltage || solarVoltage) createAxisAndSeries('right', "State/Voltage", generateDataStringForVoltage(batteryVoltage, solarVoltage))
    if (!tubingPressure && !casingPressure && !linePressure && !gasRate && !adHoc && !batteryVoltage && !solarVoltage) {
      if (state) createAxisAndSeriesState('left', "State", "State")
    }


    let cursor = chart.set("cursor", am5xy.XYCursor.new(root, {
      xAxis: xAxis,
      behavior: "zoomXY"
    }));
    ///cursor.lineY.set("visible", false);
    //Axis ranges// 
    if (state) {
      let stateEvents = getStateEvents(chartData);

      if (stateEvents.length > 0 && stateEvents.length <= 50) {

        stateEvents.forEach(element => {
          if (element.State != null) {
            const stateColor = am5.color(setShadeColor(element.State));

            let rangeDataItem = xAxis.makeDataItem({
              value: element.startTime,
              endValue: element.endTime
            });
            let range = xAxis.createAxisRange(rangeDataItem);
            range.get("grid").setAll({
              forceHidden: false,
              strokeOpacity: 0,
              location: 0.5,
              fillOpacity: 0.3,
              visible: true

            });
            range.get("axisFill").setAll({
              fill: stateColor,
              fillOpacity: 0.3,
              visible: true
            });


            if (element.State === 8 || element.State === 9 || element.State === 16 || element.State === 17) {
              const hatchA = am5.LinePattern.new(root, {
                color: stateColor,
                rotation: 45,
                strokeWidth: 0.3,  // Much thinner lines
                width: 12,         // Larger pattern size for better spacing
                height: 12,
                gap: 8,            // Increased gap between lines
              });

              range.get("axisFill").setAll({
                fill: stateColor,
                fillOpacity: 0.4,  // Reduced background opacity
                fillPattern: hatchA,
                visible: true,
              });

              // Create the perpendicular lines using a second pattern
              const hatchB = am5.LinePattern.new(root, {
                color: stateColor,
                rotation: -45,     // Perpendicular direction
                strokeWidth: 0.2,  // Same thin lines
                width: 12,
                height: 12,
                gap: 8,
              });
              const overlayItem = xAxis.makeDataItem({
                value: element.startTime,
                endValue: element.endTime
              });
              const overlayRange = xAxis.createAxisRange(overlayItem);

              overlayRange.get("grid").setAll({ visible: false, strokeOpacity: 0 });

              overlayRange.get("axisFill").setAll({
                visible: true,
                fillOpacity: 0,
                fillPattern: hatchB,
              });
            }

            // Add diagonal pattern for states (only single diagonal, not cross-hatch)
            if (element.State === 10 || element.State === 11 || element.State === 14 || element.State === 15 || element.State === 18) {
              const diagonalPattern = am5.LinePattern.new(root, {
                color: stateColor,
                rotation: 45,
                strokeWidth: 0.5,   // Slightly thicker for visibility
                width: 14,           // Much smaller pattern size
                height: 14,          // Much smaller pattern size  
                gap: 26,             // Small gap between lines
                strokeDasharray: [] // Ensure solid lines (no dashes)
              });

              range.get("axisFill").setAll({
                fill: stateColor,
                fillOpacity: 0.4,   // Much reduced background opacity
                fillPattern: diagonalPattern,
                visible: true
              });
            }

            rangeDataItem.get("grid").setAll({
              stroke: stateColor,
              fill: stateColor,
              strokeOpacity: 0,
              location: 0.5,
              fillOpacity: 0.3,
            });

          }
        });
      }

    }
    let legendRoot: am5.Root | null = null;

    if (tubingPressure || casingPressure || linePressure || gasRate || adHoc || batteryVoltage || solarVoltage || AUX1.checked || AUX2.checked || AUX3.checked || MODE.checked) {

      // Create new legend root
      legendRoot = am5.Root.new("legenddiv");

      // Custom grid layout
      const gridLayout = am5.GridLayout.new(root, {
        maxColumns: 4,
        fixedWidthGrid: true
      });

      const legend = legendRoot.container.children.push(
        am5.Legend.new(legendRoot, {
          width: am5.percent(100),
          layout: gridLayout,
          useDefaultMarker: false
        })
      );

      // Suppress default markers and labels
      legend.labels.template.set("forceHidden", true);
      legend.valueLabels.template.set("forceHidden", true);
      legend.markers.template.set("forceHidden", true);

      legend.itemContainers.template.events.on("dataitemchanged", (ev) => {
        const dataItem = ev.target.dataItem;
        const series = dataItem?.dataContext as am5xy.LineSeries;
        const name = series?.get("name") || "Unknown";

        // Dummy values
        const min = "[value]";
        const avg = "[value]";
        const max = "[value]";

        ev.target.setAll({
          layout: root.verticalLayout,
          height: 100,
          //width: 300,
          //width: am5.percent(100),
          minWidth: 320,
          maxWidth: 400,
          paddingTop: 10,
          paddingBottom: 10,
          paddingLeft: 14,
          paddingRight: 14,
          marginRight: 12,
          marginBottom: 12,
          background: am5.RoundedRectangle.new(root, {
            fill: am5.color(0x1E293B),
            stroke: series.get("stroke"),
            cornerRadiusTL: 10,
            cornerRadiusTR: 10,
            cornerRadiusBL: 10,
            cornerRadiusBR: 10
          })
        });

        ev.target.children.clear();

        // Header row
        const headerRow = am5.Container.new(root, {
          layout: root.horizontalLayout,
          width: am5.percent(100),
          centerY: am5.p50,
          marginBottom: 6
        });

        const bullet = am5.RoundedRectangle.new(root, {
          width: 14,
          height: 8,
          fill: series.get("stroke"),
          centerY: am5.p50,
          marginRight: 8,
          cornerRadiusTL: 2,
          cornerRadiusTR: 2,
          cornerRadiusBL: 2,
          cornerRadiusBR: 2
        });

        const label = am5.Label.new(root, {
          text: name,
          fontSize: 14,
          fontWeight: "600",
          fill: am5.color(0xffffff),
          centerY: am5.p50
        });

        headerRow.children.push(bullet);
        headerRow.children.push(label);

        // Value row
        const valueRow = am5.Container.new(root, {
          layout: root.horizontalLayout,
          width: am5.percent(100),
          centerY: am5.p50,
          paddingTop: 4,
          paddingBottom: 4,
        });

        const minLabel = am5.Label.new(root, {
          text: `• Min: ${min}`,
          fontSize: 12,
          fill: am5.color(0xffffff),
          marginRight: 24,
          width: am5.percent(33), // Allocate 33% of the row width
          textAlign: "center", // Center-align the text
        });

        const avgLabel = am5.Label.new(root, {
          text: `• Avg: ${avg}`,
          fontSize: 12,
          fill: am5.color(0xffffff),
          marginRight: 24,
          width: am5.percent(33), // Allocate 33% of the row width
          textAlign: "center", // Center-align the text
        });

        const maxLabel = am5.Label.new(root, {
          text: `• Max: ${max}`,
          fontSize: 12,
          fill: am5.color(0xffffff)
        });

        minLabel.setAll({ lineHeight: 5 });
        avgLabel.setAll({ lineHeight: 5 });
        maxLabel.setAll({ lineHeight: 5 });
        valueRow.children.push(minLabel);
        valueRow.children.push(avgLabel);
        valueRow.children.push(maxLabel);

        ev.target.children.push(headerRow);
        ev.target.children.push(valueRow);
      });

      // Set legend data
      legend.data.setAll(
        chart.series.values.filter(series => series.get("name") !== "State")
      );

    }
    else {
      var legend = chart.children.push(
        am5.Legend.new(root, {
          centerX: am5.percent(50),
          x: am5.percent(50),
          y: am5.percent(99),
        })
      );
    }

    xAxis.get('renderer').labels.template.set('fill', am5.color('#fff'));

    chart.appear(1000, 100);

    const labelContainer = document.getElementById("chartlabels");
    if (labelContainer) {
      labelContainer.innerHTML = "";

      chart.series.values
        .filter((s) => {
          const name = s.get("name");
          return name && name !== "" && name !== "State";
        })
        .forEach((series) => {
          const name = series.get("name");
          const color = series.get("stroke").toString();

          const tag = document.createElement("div");
          tag.className = "chart-label-tag";
          tag.style.borderColor = color;

          const swatch = document.createElement("div");
          swatch.className = "chart-label-color";
          swatch.style.backgroundColor = color;

          const labelText = document.createElement("div");
          labelText.innerText = nameMap[name] || name;

          tag.appendChild(swatch);
          tag.appendChild(labelText);
          labelContainer.appendChild(tag);
        });
    } //this is for dynamic labels. 

    // ---- Custom HTML Legend Rendering ----
    const legendDiv = document.getElementById("legenddiv");
    if (legendDiv) {
      legendDiv.innerHTML = ""; // Clear existing legend

      const filteredSeries = chart.series.values.filter((s) => s.get("name") !== "State");

      props?.onLegendCountChange?.(filteredSeries.length);

      chart.series.values
        .filter((s) => {
          const name = s.get("name");
          return name && name !== "" && name !== "State";
        })
        .forEach((series) => {
          const name = series.get("name");
          const color = series.get("stroke").toString();

          let key = seriesNameToKey[name]; // Try static mapping first

          // If not found, check AUX1/AUX2/AUX3 by matching dynamic label
          if (!key) {
            const auxMap = {
              [AUX1?.label]: "aux1",
              [AUX2?.label]: "aux2",
              [AUX3?.label]: "aux3",
              [MODE?.label]: "mode",
            };
            key = auxMap[name];
          }

          const aggregation = props?.trendData?.aggregations?.[key];


          if (!aggregation) {
          }

          const min = aggregation?.min != null ? aggregation.min.toFixed(2) : "-";
          const avg = aggregation?.mean != null ? aggregation.mean.toFixed(2) : "-";
          const max = aggregation?.max != null ? aggregation.max.toFixed(2) : "-";

          const card = document.createElement("div");
          card.className = "legend-item";
          card.style.borderColor = color;


          const header = document.createElement("div");
          header.className = "legend-header";

          const bullet = document.createElement("div");
          bullet.className = "legend-bullet";
          bullet.style.backgroundColor = color;

          const title = document.createElement("div");
          title.className = "legend-title";
          //title.textContent = nameMap[name] || name;
          title.textContent = dataObjects[name]?.label || nameMap[name] || name;


          const valueRow = document.createElement("div");
          valueRow.className = "legend-values";

          ["Min", "Avg", "Max"].forEach((label, index) => {
            const val = document.createElement("div");
            val.className = "legend-value1";
            // val.innerText = `• ${label}: ${[min, avg, max][index]}`;
            val.innerHTML = `<span class="value-bullet"></span>${label}: ${[min, avg, max][index]}`;
            valueRow.appendChild(val);
          });

          header.appendChild(bullet);
          header.appendChild(title);

          card.appendChild(header);
          card.appendChild(valueRow);

          legendDiv.appendChild(card);
        });
    }

    var tooltip = am5.Tooltip.new(root, {});

    chart.plotContainer.set("tooltipPosition", "pointer");
    chart.plotContainer.set("tooltipText", "a");
    chart.plotContainer.set("tooltip", tooltip);

    tooltip.label.adapters.add("text", function (text, target) {
      text = ""
      var i = 0;

      let show = true;
      chart.series.each(function (series) {
        if (series.get("name") === "State") {
          return;
        }
        var tooltipDataItem = series.get("tooltipDataItem");
        if (tooltipDataItem) {
          if (i !== 0) {
            text += "\n";
          }


          if (show) {
            let d = moment(tooltipDataItem.get("valueX")).format("HH:mm:ss - MMM DD, YYYY");
            text += "●[/] [bold]" + "Date" + ':[/] ' + d + "\n";
            show = false;
          }
          text += '[' + series.get("stroke").toString() + ']●[/] [bold width:100px]' + series.get("name") + ':[/] ' + (tooltipDataItem.get("valueY") == null ? "" : tooltipDataItem.get("valueY").toFixed(2));
        }
        i++;
      });
      return text
    });
    tooltip.get("background").setAll({
      fill: am5.color(0xffffff),
      fillOpacity: 0.4
    });

    return () => {
      root?.dispose();
      chart?.dispose();
      legendRoot?.dispose();
    };
  }, [props.trendData, unit, hasIntermittentMode]);

  return <>
    {/* Top label strip */}
    <div id="chartlabels" className="chart-label-strip" />
    <div id="chartdiv" className={props?.isMobile ? 'min-w-[1000px]' : ''} style={{ width: '100%', height: '100%' }}></div>
    {/* <div id="legendwrapper" className={props?.isMobile ? 'min-w-[1000px]' : ''} style={{ marginTop: "3%", marginLeft: "auto", overflow:"hidden" }}>
      <div id="legenddiv"></div>
    </div> */}
  </>;
};

export default AnalysisChart;